import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-md font-semibold transition-transform duration-fast ease-standard focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:pointer-events-none disabled:opacity-60',
  {
    variants: {
      variant: {
        default:
          'bg-[var(--color-bg-accent)] text-white shadow-floating hover:scale-[1.01] active:scale-[0.99] focus-visible:outline-white',
        secondary:
          'bg-[rgba(255,255,255,0.12)] text-foreground shadow-card hover:scale-[1.01] active:scale-[0.99] focus-visible:outline-white',
        outline:
          'border border-[rgba(255,255,255,0.2)] text-foreground hover:bg-[rgba(255,255,255,0.08)] focus-visible:outline-white',
        ghost:
          'bg-transparent text-foreground hover:bg-[rgba(255,255,255,0.08)] focus-visible:outline-white'
      },
      size: {
        default: 'px-lg py-sm text-base',
        sm: 'px-md py-xs text-sm',
        lg: 'px-xl py-md text-lg'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref as never}
        {...props}
      />
    );
  }
);

Button.displayName = 'Button';
